import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table, Input, Label, Row, Col } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { rangoTalla } from "../../../desglosePedido";

// Sube la PI (proforma invoice) del proveedor en Excel y actualiza la orden: cada
// renglon toma la cantidad y el precio de la PI y se guarda la cantidad original
// la primera vez, asi quedan las dos. Lo que no viene en el archivo no se toca.
//
// El layout sale de la propia orden, un renglon por modelo, color y talla, para
// que el proveedor solo llene PI QTY y PI PRICE.
const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const num = (n) => new Intl.NumberFormat("en-US").format(Number(n) || 0);
const llave = (t) => String(t ?? "").trim().toUpperCase();
const COLUMNAS = ["MODEL", "LINE", "COLOR", "SIZE", "ORIGINAL QTY", "PI QTY", "PI PRICE"];
// acepta tambien encabezados en espanol, por si el archivo viene de otro lado
const dame = (fila, ...nombres) => {
  const k = Object.keys(fila).find((c) => nombres.includes(llave(c)));
  return k ? fila[k] : "";
};
const numero = (v) => {
  if (v === "" || v === undefined || v === null) return null;
  const n = Number(String(v).replace(/[$,\s]/g, ""));
  return Number.isFinite(n) ? n : null;
};

function SubirPIModal({ abierto, cerrar, orden, coloresCat, tallasCat, onAplicada }) {
  const [lineas, setLineas] = useState([]);
  const [pi, setPi] = useState("");
  const [fechaPi, setFechaPi] = useState("");
  const [archivo, setArchivo] = useState("");
  const [filas, setFilas] = useState([]);
  const [avisos, setAvisos] = useState([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!abierto || !orden) return;
    setFilas([]); setAvisos([]); setArchivo("");
    setPi(orden.pi && orden.pi !== "NA" ? orden.pi : "");
    setFechaPi(orden.fechaPi && orden.fechaPi !== "NA" ? orden.fechaPi : "");
    axios.get(`${process.env.REACT_APP_URL_ARTICULOS_OC}/ordenesCompra/${orden._id}`, { headers: headers() })
      .then((r) => setLineas((r.data || []).filter((a) => a.is_active !== "No" && a.articulos?.[0]).map((a) => ({
        id: a._id, idArticulo: a.articulos[0]._id, modelo: a.articulos[0].codigo, renglon: a.renglon || 1,
        color: a.colores?.[0]?.name || "", talla: a.tallas?.[0]?.name || "",
        cantidad: Number(a.cantidad) || 0, embarcado: Number(a.embarcado) || 0, precio: Number(a.precio) || 0,
        original: a.cantidadOriginal ?? null,
      })).sort((x, y) => (x.renglon - y.renglon) || (x.color > y.color ? 1 : x.color < y.color ? -1 : rangoTalla(x.talla) - rangoTalla(y.talla)))))
      .catch((e) => Swal.fire({ icon: "error", title: "No se pudieron traer los artículos de la orden", text: e.message }));
  }, [abierto, orden]); // eslint-disable-line

  function descargaLayout() {
    const wb = XLSX.utils.book_new();
    const hoja = XLSX.utils.aoa_to_sheet([
      COLUMNAS,
      ...lineas.map((l) => [l.modelo, l.renglon, l.color, l.talla, l.original ?? l.cantidad, l.cantidad, l.precio]),
    ]);
    hoja["!cols"] = COLUMNAS.map((c) => ({ wch: Math.max(12, c.length + 4) }));
    XLSX.utils.book_append_sheet(wb, hoja, "PI");
    XLSX.writeFile(wb, `PI-PO-${orden.numero}.xlsx`);
  }

  function leeArchivo(file) {
    if (!file) return;
    setArchivo(file.name); setFilas([]); setAvisos([]);
    const lector = new FileReader();
    lector.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const hoja = wb.Sheets.PI || wb.Sheets[wb.SheetNames[0]];
        compara(XLSX.utils.sheet_to_json(hoja, { defval: "" }));
      } catch (err) {
        Swal.fire({ icon: "error", title: "No se pudo leer el archivo", text: "Revisa que sea el Excel de la PI." });
      }
    };
    lector.readAsArrayBuffer(file);
  }

  // Casa cada renglon del archivo con el de la orden por modelo, color y talla (y el
  // numero de renglon si lo trae) y arma la comparacion antes de guardar nada.
  function compara(renglones) {
    const av = [];
    const usados = new Set();
    const resultado = [];
    renglones.forEach((f, i) => {
      const fila = i + 2;
      const modelo = llave(dame(f, "MODEL", "MODELO", "ITEM", "ITEM NO.", "ARTICLE"));
      const color = llave(dame(f, "COLOR"));
      const talla = llave(dame(f, "SIZE", "TALLA"));
      if (!modelo && !color && !talla) return;
      const qty = numero(dame(f, "PI QTY", "CANTIDAD PI", "QTY", "CANTIDAD"));
      const precio = numero(dame(f, "PI PRICE", "PRECIO PI", "PRICE", "PRECIO", "COSTO"));
      const linea = numero(dame(f, "LINE", "RENGLON"));
      if (qty === null || qty < 0) { av.push(`Renglón ${fila}: ${modelo} ${color} ${talla} sin cantidad; no se toma.`); return; }
      const candidatas = lineas.filter((l) => llave(l.modelo) === modelo && llave(l.color) === color && llave(l.talla) === talla && !usados.has(l.id));
      const l = candidatas.find((x) => linea === null || x.renglon === linea) || candidatas[0];
      if (l) {
        usados.add(l.id);
        if (qty < l.embarcado) { av.push(`Renglón ${fila}: ${modelo} ${color} ${talla} queda abajo de lo embarcado (${num(l.embarcado)}); no se toma.`); return; }
        resultado.push({ ...l, nuevaCantidad: qty, nuevoPrecio: precio ?? l.precio, esNueva: false });
        return;
      }
      // color o talla que la orden no traia: solo de un modelo de la orden y del catalogo
      const delModelo = lineas.find((x) => llave(x.modelo) === modelo);
      const col = coloresCat.find((c) => llave(c.name) === color);
      const tal = tallasCat.find((t) => llave(t.name) === talla);
      if (!delModelo) { av.push(`Renglón ${fila}: el modelo ${modelo} no está en esta orden; no se toma.`); return; }
      if (!col || !tal) { av.push(`Renglón ${fila}: ${color} ${talla} no existe en el catálogo; no se toma.`); return; }
      if (qty === 0) return;
      resultado.push({
        id: `nuevo${i}`, idArticulo: delModelo.idArticulo, modelo: delModelo.modelo, renglon: linea || delModelo.renglon,
        color: col.name, talla: tal.name, idColor: col._id, idTalla: tal._id,
        cantidad: 0, embarcado: 0, precio: precio ?? delModelo.precio, original: 0,
        nuevaCantidad: qty, nuevoPrecio: precio ?? delModelo.precio, esNueva: true,
      });
    });
    setFilas(resultado); setAvisos(av);
  }

  const cambiadas = filas.filter((f) => f.esNueva || f.nuevaCantidad !== f.cantidad || f.nuevoPrecio !== f.precio);
  const noVienen = lineas.length - filas.filter((f) => !f.esNueva).length;
  const totalAntes = lineas.reduce((s, l) => s + l.cantidad, 0);
  const totalDespues = totalAntes + filas.reduce((s, f) => s + f.nuevaCantidad - f.cantidad, 0);

  async function aplica() {
    if (!pi.trim()) {
      Swal.fire({ icon: "warning", title: "Falta el número de PI", text: "Captura el número de la PI." });
      return;
    }
    const ok = await Swal.fire({
      icon: "question", title: `¿Actualizar la orden ${orden.numero} con la PI ${pi.trim()}?`,
      html: `${cambiadas.length} renglón(es) cambian · de ${num(totalAntes)} a <b>${num(totalDespues)}</b> piezas.<br>Se conserva la cantidad original.`,
      showCancelButton: true, confirmButtonText: "Sí, actualizar", cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    setGuardando(true);
    try {
      await axios.post(`${process.env.REACT_APP_URL_ORDENES_COMPRA}/pi/${orden._id}`, {
        pi: pi.trim(), fechaPi,
        actualizar: filas.filter((f) => !f.esNueva).map((f) => ({ id: f.id, cantidad: f.nuevaCantidad, precio: f.nuevoPrecio })),
        nuevos: filas.filter((f) => f.esNueva).map((f) => ({
          articulos: f.idArticulo, renglon: f.renglon, colores: f.idColor, tallas: f.idTalla, cantidad: f.nuevaCantidad, precio: f.nuevoPrecio,
        })),
      }, { headers: headers() });
      setGuardando(false);
      await Swal.fire({ icon: "success", title: "Orden actualizada con la PI", timer: 1300, showConfirmButton: false });
      cerrar();
      if (onAplicada) onAplicada();
    } catch (error) {
      setGuardando(false);
      Swal.fire({ icon: "error", title: "No se pudo actualizar", text: error.response?.data?.message || error.message });
    }
  }

  return (
    <Modal size="lg" isOpen={abierto} toggle={cerrar}>
      <ModalHeader toggle={cerrar}>Subir PI · Orden {orden?.numero} {orden?.proveedores ? `· ${orden.proveedores}` : ""}</ModalHeader>
      <ModalBody>
        <Row>
          <Col md={6}>
            <Label style={{ fontWeight: 600 }}>1. El layout</Label>
            <div>
              <Button size="sm" color="success" onClick={descargaLayout} disabled={!lineas.length}>
                <i className="fas fa-download" /> Descargar layout
              </Button>
            </div>
            <small style={{ color: "#64748b" }}>Trae la orden; el proveedor llena PI QTY y PI PRICE.</small>
          </Col>
          <Col md={6}>
            <Label style={{ fontWeight: 600 }}>2. Súbelo lleno</Label>
            <Input type="file" accept=".xlsx,.xls"
              onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; leeArchivo(f); }} />
            <small style={{ color: "#64748b" }}>{archivo || "Lo que no venga en el archivo no se toca."}</small>
          </Col>
        </Row>
        <Row style={{ marginTop: 8 }}>
          <Col md={6}>
            <Label>No. PI</Label>
            <Input bsSize="sm" value={pi} onChange={(e) => setPi(e.target.value)} />
          </Col>
          <Col md={6}>
            <Label>Fecha PI</Label>
            <Input bsSize="sm" type="date" value={fechaPi} onChange={(e) => setFechaPi(e.target.value)} />
          </Col>
        </Row>
        {avisos.length ? (
          <div style={{ maxHeight: 110, overflowY: "auto", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: 8, margin: "10px 0", fontSize: 12 }}>
            {avisos.map((a, i) => <div key={i}>{a}</div>)}
          </div>
        ) : null}
        {filas.length ? (
          <div style={{ maxHeight: 320, overflowY: "auto", marginTop: 10 }}>
            <Table size="sm" bordered>
              <thead>
                <tr>
                  <th>Modelo</th><th>Color</th><th>Talla</th>
                  <th className="text-right">Original</th><th className="text-right">Actual</th><th className="text-right">PI</th>
                  <th className="text-right">Dif.</th><th className="text-right">Precio PI</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f) => {
                  const dif = f.nuevaCantidad - f.cantidad;
                  return (
                    <tr key={f.id} style={f.esNueva || dif !== 0 || f.nuevoPrecio !== f.precio ? { background: "#fffbeb" } : undefined}>
                      <td>{f.modelo}</td><td>{f.color}{f.esNueva ? " (nuevo)" : ""}</td><td>{f.talla}</td>
                      <td className="text-right">{num(f.original ?? f.cantidad)}</td>
                      <td className="text-right">{num(f.cantidad)}</td>
                      <td className="text-right"><b>{num(f.nuevaCantidad)}</b></td>
                      <td className="text-right">{dif > 0 ? `+${num(dif)}` : num(dif)}</td>
                      <td className="text-right">{f.nuevoPrecio}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        ) : (
          <p style={{ color: "#9ca3af", marginTop: 14 }}>Sube el archivo para comparar la PI contra la orden.</p>
        )}
      </ModalBody>
      <ModalFooter>
        <Label style={{ marginRight: "auto", color: "#64748b", fontSize: 12 }}>
          {filas.length ? `${cambiadas.length} renglón(es) cambian · ${noVienen} no vienen en el archivo · ${num(totalAntes)} → ${num(totalDespues)} pzas` : ""}
        </Label>
        <Button color="success" onClick={aplica} disabled={guardando || !filas.length}>
          {guardando ? "Actualizando..." : "Actualizar orden"}
        </Button>
        <Button color="secondary" onClick={cerrar}>Cerrar</Button>
      </ModalFooter>
    </Modal>
  );
}

export default SubirPIModal;
